// frontend/src/pages/PaychequeList/components/PaychequeRow.tsx
// One <tr> for a single paycheque in the main list.
// Owns row presentation only; all actions bubble up via callbacks.

import React from "react";
import {
  MoreVertical,
  Printer,
  Eye,
  RotateCcw,
  FileText,
  Check,
} from "lucide-react";
import type { Paycheque } from "../types";
import { fmtDate, fmtMoney, initialsOf } from "../helpers";
import {
  C,
  FONT,
  TABULAR,
  tdStyle,
  menuItem,
} from "../styles";

interface PaychequeRowProps {
  paycheque: Paycheque;
  privacy: boolean;
  isEditingCheque: boolean;
  chequeInput: string;
  savingCheque: boolean;
  isKebabOpen: boolean;
  onNavigateToDetail: () => void;
  onPrintPayStub: () => void;
  onOpenCheque: () => void;
  onStartChequeEdit: () => void;
  onChequeInputChange: (value: string) => void;
  onSaveCheque: () => void;
  onCancelChequeEdit: () => void;
  onToggleKebab: () => void;
  onCloseKebab: () => void;
  onRequestVoid: () => void;
}

export default function PaychequeRow({
  paycheque: p,
  privacy,
  isEditingCheque,
  chequeInput,
  savingCheque,
  isKebabOpen,
  onNavigateToDetail,
  onPrintPayStub,
  onOpenCheque,
  onStartChequeEdit,
  onChequeInputChange,
  onSaveCheque,
  onCancelChequeEdit,
  onToggleKebab,
  onCloseKebab,
  onRequestVoid,
}: PaychequeRowProps): React.ReactElement {
  const status = p.status || "pending";
  const isVoided = status === "voided";
  const method = (p.payment_method || p.pay_method || "").toLowerCase();
  const isCheq = method.includes("cheque") || method.includes("check");

  const statusColor =
    status === "paid" || status === "issued" ? C.brandDark :
    status === "voided" ? C.grey :
    C.amber;
  const statusBg =
    status === "paid" || status === "issued" ? C.brandBg :
    status === "voided" ? C.greyBg :
    C.amberBg;
  const statusLabel =
    status === "paid" ? "Paid" :
    status === "issued" ? "Issued" :
    status === "voided" ? "Voided" :
    "Pending";

  return (
    <tr style={{ borderBottom: "1px solid " + C.lineSoft, opacity: isVoided ? 0.6 : 1 }}>
      <td style={tdStyle}>
        <input type="checkbox" onClick={(e) => e.stopPropagation()} style={{ cursor: "pointer" }} />
      </td>

      <td style={{ ...tdStyle, ...TABULAR, cursor: "pointer" }} onClick={onNavigateToDetail}>
        {fmtDate(p.pay_date)}
      </td>

      <td style={{ ...tdStyle, cursor: "pointer" }} onClick={onNavigateToDetail}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.brandBg, color: C.brandDark, display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700 }}>
            {initialsOf(p.employee_name)}
          </div>
          <span style={{ color: C.ink, fontWeight: 600 }}>{p.employee_name || "Unnamed"}</span>
        </div>
      </td>

      <td style={{ ...tdStyle, textAlign: "right", ...TABULAR }}>
        {privacy ? <span style={{ letterSpacing: 4 }}>••••</span> : fmtMoney(p.gross_pay)}
      </td>

      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, ...TABULAR }}>
        {privacy ? <span style={{ letterSpacing: 4 }}>••••</span> : fmtMoney(p.net_pay)}
      </td>

      <td style={tdStyle}>{p.payment_method || p.pay_method || ""}</td>

      <td style={tdStyle}>
        {isEditingCheque ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="text"
              value={chequeInput}
              onChange={(e) => onChequeInputChange(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
              onKeyDown={(e) => {
                if (e.key === "Enter") onSaveCheque();
                if (e.key === "Escape") onCancelChequeEdit();
              }}
              autoFocus
              disabled={savingCheque}
              placeholder="1042"
              maxLength={10}
              inputMode="numeric"
              style={{
                width: 100,
                padding: "6px 8px",
                border: "1.5px solid " + C.brand,
                borderRadius: 6,
                fontFamily: FONT,
                fontSize: 13,
                color: C.ink,
                outline: "none",
                fontVariantNumeric: "tabular-nums",
              }}
            />
            <button
              onClick={onSaveCheque}
              disabled={savingCheque}
              style={{ padding: "4px 8px", background: C.brand, border: "none", borderRadius: 6, color: "#fff", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
            >
              {savingCheque ? "..." : "Save"}
            </button>
          </div>
        ) : p.cheque_number ? (
          <span
            onClick={(e) => { e.stopPropagation(); onOpenCheque(); }}
            style={{ color: C.brandDark, fontWeight: 700, cursor: "pointer", textDecoration: "underline", ...TABULAR }}
          >
            {p.cheque_number}
          </span>
        ) : isCheq ? (
          <span
            onClick={(e) => { e.stopPropagation(); onStartChequeEdit(); }}
            style={{ color: C.brandDark, fontWeight: 700, cursor: "pointer", textDecoration: "underline" }}
          >
            + Add cheque number
          </span>
        ) : (
          <span style={{ color: C.ink }}>Direct deposit</span>
        )}
      </td>

      <td style={tdStyle}>
        <span style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontWeight: 700,
          color: statusColor,
          background: statusBg,
          padding: "3px 10px",
          borderRadius: 5,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}>
          <span style={{ width: 6, height: 6, background: statusColor, borderRadius: "50%" }} />
          {statusLabel}
        </span>
      </td>

      <td style={{ ...tdStyle, textAlign: "right", position: "relative", width: 60 }} className="row-kebab">
        <button
          onClick={(e) => { e.stopPropagation(); onToggleKebab(); }}
          style={{ width: 30, height: 30, border: "none", background: "transparent", borderRadius: 6, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", color: C.ink }}
        >
          <MoreVertical size={16} />
        </button>
        {isKebabOpen && (
          <div
            className="row-kebab"
            style={{
              position: "absolute",
              top: 40,
              right: 10,
              background: C.card,
              border: "1px solid " + C.line,
              borderRadius: 10,
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              padding: "6px 0",
              minWidth: 200,
              zIndex: 50,
              textAlign: "left",
            }}
          >
            <button onClick={() => { onCloseKebab(); onNavigateToDetail(); }} style={menuItem}>
              <Eye size={14} /> View pay stub
            </button>
            <button onClick={onPrintPayStub} style={menuItem}>
              <Printer size={14} /> Print pay stub
            </button>
            {isCheq && p.cheque_number && (
              <button onClick={onOpenCheque} style={menuItem}>
                <FileText size={14} /> View cheque
              </button>
            )}
            {isCheq && !p.cheque_number && (
              <button
                onClick={() => { onCloseKebab(); onStartChequeEdit(); }}
                style={Object.assign({}, menuItem, { color: C.brandDark, fontWeight: 700 })}
              >
                + Add cheque number
              </button>
            )}
            {status !== "voided" && (
              <>
                <div style={{ height: 1, background: C.line, margin: "4px 0" }} />
                <button
                  onClick={() => { onCloseKebab(); onRequestVoid(); }}
                  style={Object.assign({}, menuItem, { color: C.err })}
                >
                  <RotateCcw size={14} /> Void this paycheque
                </button>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}